import { type ReactNode } from "react";

import { Alert, Button, Group, List, Stack, Table, Text, Title } from "@mantine/core";

import { NextLink } from "@/app/lib/components/next/NextLink";

import type { Section } from "../_content/types";
import { renderInline, toParagraphs } from "./inline";

export function renderSection(section: Section): ReactNode {
  switch (section.type) {
    case "prose":
      return (
        <Stack gap="sm">
          {section.heading && <Title order={2}>{section.heading}</Title>}
          {toParagraphs(section.body).map((paragraph, index) => (
            <Text key={index}>{renderInline(paragraph)}</Text>
          ))}
        </Stack>
      );

    case "list":
      return (
        <Stack gap="sm">
          {section.heading && <Title order={2}>{section.heading}</Title>}
          <List type={section.ordered ? "ordered" : "unordered"} spacing="xs">
            {section.items.map((item, index) => (
              <List.Item key={index}>{renderInline(item)}</List.Item>
            ))}
          </List>
        </Stack>
      );

    case "table":
      return (
        <Stack gap="sm">
          {section.heading && <Title order={2}>{section.heading}</Title>}
          {/* maw 100% is required or the scroll container expands and never scrolls.
              maxWidth is not a valid prop and leaks to the DOM; use the Mantine maw style prop. */}
          <Table.ScrollContainer minWidth={640} maw="100%">
            <Table striped withTableBorder withColumnBorders verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  {section.columns.map((column, index) => (
                    <Table.Th key={index}>{column}</Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {section.rows.map((row, rowIndex) => (
                  <Table.Tr key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                      <Table.Td key={cellIndex}>{renderInline(cell)}</Table.Td>
                    ))}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Stack>
      );

    case "callout":
      return (
        <Alert color="blue" title={section.heading}>
          {renderInline(section.body)}
        </Alert>
      );

    case "cta":
      return (
        <Stack gap="sm">
          <Title order={2}>{section.heading}</Title>
          {section.body && <Text>{renderInline(section.body)}</Text>}
          <Group>
            <Button
              component={NextLink}
              href={section.href}
              variant="gradient"
              gradient={{ from: "blue", to: "cyan" }}
            >
              {section.label}
            </Button>
          </Group>
        </Stack>
      );
  }
}
